import { FC, ReactNode } from 'react';
import clsx from 'clsx';

import { makeStyles, Theme } from '@material-ui/core/styles';

const useStyles = makeStyles<Theme, { size: 'sm' | 'xs' }>(() => ({
  label: {
    display: 'inline-flex',
    flexDirection: 'column',
    fontSize: props => (props.size === 'xs' ? 12 : 13),
    borderRadius: 4,
    padding: props => (props.size === 'xs' ? '4px 8px 4px 4px' : '4px 8px 4px 6px'),
  },
  labelWrapper: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 4,
    fontWeight: 600,
  },
  labelNoIcon: {
    padding: '4px 8px',
  },
  labelSpace: {
    margin: '4px 0',
  },
  labelClickable: {
    cursor: 'pointer',
  },
  labelInfo: {
    color: '#4050B5',
    background: '#E8EAF6',
  },
  labelSuccess: {
    color: '#3A6F6E',
    background: '#E7F1F1',
  },
  labelError: {
    color: '#B71C1C',
    background: '#FFEBEE',
  },
  labelWarn: {
    color: '#80652E',
    background: '#F8EFD1',
  },
  labelDisabled: {
    color: 'rgba(0, 0, 0, 0.26)',
    background: 'rgba(0, 0, 0, 0.12)',
  },
}));

interface TagLabelProps {
  readOnly?: boolean;
  disabled?: boolean;
  space?: boolean;
  withContent?: boolean;
  icon?: ReactNode;
  size?: 'sm' | 'xs';
  type: 'info' | 'success' | 'error' | 'warn';
  label?: string;
  children?: ReactNode;
  style?: React.CSSProperties;
  onClick?: () => void;
  // Makes a clickable label reachable with Tab and activatable with Enter/Space.
  focusable?: boolean;
}

const TagLabel: FC<TagLabelProps> = ({
  readOnly,
  disabled: disabledProp,
  space,
  withContent,
  type,
  style,
  icon,
  size = 'xs',
  label,
  children,
  onClick,
  focusable,
}) => {
  const classes = useStyles({ size });

  const disabled = disabledProp || (readOnly && !!onClick);
  const isKeyboardClickable = !!focusable && !!onClick && !disabled;

  return (
    <div>
      <div
        className={clsx(
          classes.label,
          space && classes.labelSpace,
          !icon && classes.labelNoIcon,
          disabled && classes.labelDisabled,
          !disabled && clsx(
            onClick && classes.labelClickable,
            type === 'info' && classes.labelInfo,
            type === 'success' && classes.labelSuccess,
            type === 'warn' && classes.labelWarn,
            type === 'error' && classes.labelError
          )
        )}
        style={style}
        onClick={() => !disabled && onClick?.()}
        role={isKeyboardClickable ? 'button' : undefined}
        tabIndex={isKeyboardClickable ? 0 : undefined}
        onKeyDown={
          isKeyboardClickable
            ? event => {
                if (event.key === 'Enter' || event.key === ' ') {
                  event.preventDefault();
                  onClick?.();
                }
              }
            : undefined
        }
      >
        <div className={classes.labelWrapper}>
          {icon}
          <span style={{ verticalAlign: 'middle' }}>{label || (!withContent && children)}</span>
        </div>

        {withContent && <div>{children}</div>}
      </div>
    </div>
  );
};

export default TagLabel;
